import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    useTheme,
    useMediaQuery,
    Grid,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    ListItemIcon,
    ListItemText,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Menu,
    MenuItem,
    Fab,
    LinearProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ItemPresupuesto, Obra } from '../../types';
import { DragDropContext, Droppable, Draggable, DropResult, DraggableProvided, DroppableProvided } from '@hello-pangea/dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import { Rubro } from '../../types';
import { aiAssistantService } from '../../services/aiAssistantService';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ConstructionIcon from '@mui/icons-material/Construction';
import CategoryIcon from '@mui/icons-material/Category';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SavingsIcon from '@mui/icons-material/Savings';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EngineeringIcon from '@mui/icons-material/Engineering';



interface PresupuestoTableProps {
    items: ItemPresupuesto[];
    rubros: Rubro[];
    onUpdate: (id: string, updates: Partial<ItemPresupuesto>) => Promise<void>;
    onAdd: (item: Omit<ItemPresupuesto, 'id'>) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    onAddRubro: (rubro: Omit<Rubro, 'id'>) => Promise<string>;
    onDeleteRubro: (id: string) => Promise<void>;
    onBulkDelete?: (itemIds: string[], rubroIds: string[]) => Promise<void>;
    onUpdateRubro: (id: string, updates: Partial<Rubro>) => Promise<void>;
    onDragEnd: (result: DropResult) => void;
    obra?: Obra;
    readOnly?: boolean;
}

const BufferedInput: React.FC<{
    value: string | number;
    onChange: (value: string | number) => void;
    type?: string;
    variant?: "standard" | "outlined" | "filled";
    inputProps?: any;
    InputProps?: any;
    disabled?: boolean;
    sx?: any;
    placeholder?: string;
    size?: "small" | "medium";
    fullWidth?: boolean;
}> = ({ value: initialValue, onChange, ...props }) => {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
    };

    const handleBlur = () => {
        if (value !== initialValue) {
            onChange(value);
        }
    };

    return (
        <TextField
            {...props}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
        />
    );
};

export const PresupuestoTable: React.FC<PresupuestoTableProps> = ({
    items,
    rubros,
    onUpdate,
    onAdd,
    onDelete,
    onAddRubro,
    onDeleteRubro,
    onBulkDelete,
    onUpdateRubro,
    onDragEnd,
    obra,
    readOnly = false
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const stickyColumnWidth = isSmallMobile ? 120 : 250;

    const [openAdd, setOpenAdd] = useState(false);
    const [openAddRubro, setOpenAddRubro] = useState(false);
    const [newRubroName, setNewRubroName] = useState('');
    const [editingRubro, setEditingRubro] = useState<Rubro | null>(null);

    const [newItem, setNewItem] = useState<Partial<ItemPresupuesto>>({
        nombre: '',
        unidadMedida: '',
        cantidad: 0,
        cantidadReal: 0,
        precioUnitario: 0,
        costoManoObra: 0,
        costoMateriales: 0,
        duracionDias: 1,
        fechaInicioEstimada: new Date().toISOString().split('T')[0],
        rubroId: ''
    });
    const [editingItem, setEditingItem] = useState<ItemPresupuesto | null>(null);

    // Photo analysis state
    const [openPhotoAnalyzer, setOpenPhotoAnalyzer] = useState(false);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [photoBase64, setPhotoBase64] = useState<string | null>(null);
    const [photoMimeType, setPhotoMimeType] = useState<string>('image/jpeg');
    const [analyzing, setAnalyzing] = useState(false);
    const [analyzeError, setAnalyzeError] = useState<string | null>(null);
    const [analyzedItems, setAnalyzedItems] = useState<{ rubro: string; nombre: string; unidadMedida: string; cantidad: number; precioUnitario: number; selected: boolean }[]>([]);
    const [addingItems, setAddingItems] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Selection state for bulk actions
    const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
    const [selectedRubroIds, setSelectedRubroIds] = useState<Set<string>>(new Set());
    const [openConfirmBulkDelete, setOpenConfirmBulkDelete] = useState(false);
    const [isBulkSelectionMode, setIsBulkSelectionMode] = useState(false);
    
    // Mobile actions menu
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);
    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const toggleSelectItem = (id: string) => {
        const newSet = new Set(selectedItemIds);
        if (newSet.has(id)) newSet.delete(id);
        else newSet.add(id);
        setSelectedItemIds(newSet);
    };

    const toggleSelectRubro = (rubroId: string, rubroItems: ItemPresupuesto[]) => {
        const newRubroSet = new Set(selectedRubroIds);
        const newItemSet = new Set(selectedItemIds);

        if (newRubroSet.has(rubroId)) {
            newRubroSet.delete(rubroId);
            // Deselect items of this rubro
            rubroItems.forEach(i => newItemSet.delete(i.id));
        } else {
            newRubroSet.add(rubroId);
            // Select items of this rubro
            rubroItems.forEach(i => newItemSet.add(i.id));
        }
        
        setSelectedRubroIds(newRubroSet);
        setSelectedItemIds(newItemSet);
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedItemIds(new Set(items.map(i => i.id)));
            setSelectedRubroIds(new Set(rubros.map(r => r.id)));
        } else {
            setSelectedItemIds(new Set());
            setSelectedRubroIds(new Set());
        }
    };

    const handleBulkDelete = async () => {
        try {
            if (onBulkDelete) {
                await onBulkDelete(Array.from(selectedItemIds), Array.from(selectedRubroIds));
            } else {
                // Fallback for individual deletions if onBulkDelete is not provided
                // Delete items first
                for (const id of Array.from(selectedItemIds)) {
                    await onDelete(id);
                }
                // Delete rubros
                for (const id of Array.from(selectedRubroIds)) {
                    await onDeleteRubro(id);
                }
            }
            setSelectedItemIds(new Set());
            setSelectedRubroIds(new Set());
            setOpenConfirmBulkDelete(false);
            setIsBulkSelectionMode(false); // Also disable selection mode after deletion
        } catch (err) {
            console.error("Error in bulk delete:", err);
        }
    };

    const handleCreateOrUpdate = async () => {
        if (newItem.nombre && newItem.unidadMedida) {
            if (editingItem) {
                await onUpdate(editingItem.id, {
                    nombre: newItem.nombre,
                    unidadMedida: newItem.unidadMedida,
                    cantidad: Number(newItem.cantidad),
                    cantidadReal: Number(newItem.cantidadReal) || 0,
                    precioUnitario: Number(newItem.precioUnitario),
                    costoManoObra: Number(newItem.costoManoObra) || 0,
                    costoMateriales: Number(newItem.costoMateriales) || 0,
                    rubroId: newItem.rubroId || undefined
                });
            } else {
                await onAdd({
                    nombre: newItem.nombre,
                    unidadMedida: newItem.unidadMedida,
                    cantidad: Number(newItem.cantidad),
                    cantidadReal: Number(newItem.cantidadReal) || 0,
                    precioUnitario: Number(newItem.precioUnitario),
                    costoManoObra: Number(newItem.costoManoObra) || 0,
                    costoMateriales: Number(newItem.costoMateriales) || 0,
                    obraId: obra?.id || '',
                    duracionDias: 1,
                    fechaInicioEstimada: new Date().toISOString().split('T')[0],
                    rubroId: newItem.rubroId || undefined,
                    completado: false
                });
            }
            setOpenAdd(false);
            setEditingItem(null);
            setNewItem({ nombre: '', unidadMedida: '', cantidad: 0, cantidadReal: 0, precioUnitario: 0, costoManoObra: 0, costoMateriales: 0, duracionDias: 1, fechaInicioEstimada: new Date().toISOString().split('T')[0], rubroId: '' });
        }
    };

    const openEditDialog = (item: ItemPresupuesto) => {
        setEditingItem(item);
        setNewItem({
            nombre: item.nombre,
            unidadMedida: item.unidadMedida,
            cantidad: item.cantidad,
            cantidadReal: item.cantidadReal || 0,
            precioUnitario: item.precioUnitario,
            costoManoObra: item.costoManoObra || 0,
            costoMateriales: item.costoMateriales || 0,
            rubroId: item.rubroId
        });
        setOpenAdd(true);
    };

    const openAddItemWithRubro = (rubroId: string) => {
        setEditingItem(null);
        setNewItem({
            nombre: '',
            unidadMedida: '',
            cantidad: 0,
            cantidadReal: 0,
            precioUnitario: 0,
            costoManoObra: 0,
            costoMateriales: 0,
            duracionDias: 1,
            fechaInicioEstimada: new Date().toISOString().split('T')[0],
            rubroId: rubroId
        });
        setOpenAdd(true);
    };

    // Photo analysis handlers
    const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAnalyzeError(null);
        setAnalyzedItems([]);
        setPhotoMimeType(file.type || 'image/jpeg');
        const reader = new FileReader();
        reader.onload = (ev) => {
            const dataUrl = ev.target?.result as string;
            setPhotoPreview(dataUrl);
            // Extract base64 without the data URL prefix
            const base64 = dataUrl.split(',')[1];
            setPhotoBase64(base64);
        };
        reader.readAsDataURL(file);
    };

    const handleAnalyzePhoto = async () => {
        if (!photoBase64) return;
        setAnalyzing(true);
        setAnalyzeError(null);
        setAnalyzedItems([]);
        try {
            const items = await aiAssistantService.analyzePresupuestoImage(photoBase64, photoMimeType);
            if (items.length === 0) {
                setAnalyzeError('No se encontraron ítems en la imagen. Intentá con una foto más clara del presupuesto.');
            } else {
                setAnalyzedItems(items.map(i => ({ ...i, selected: true })));
            }
        } catch (err: any) {
            setAnalyzeError(err.message || 'Error al analizar la imagen.');
        } finally {
            setAnalyzing(false);
        }
    };

    const handleAddAnalyzedItems = async () => {
        const toAdd = analyzedItems.filter(i => i.selected);
        if (toAdd.length === 0) return;
        setAddingItems(true);
        try {
            // Map to track rubro name to rubro ID
            const rubroNameToId: { [name: string]: string } = {};
            
            // Populate map with existing rubros
            rubros.forEach(r => {
                rubroNameToId[r.nombre.toLowerCase().trim()] = r.id;
            });

            for (const item of toAdd) {
                let rubroId = undefined;
                const cleanRubroName = (item.rubro || 'Sin Rubro').trim();
                const rubroKey = cleanRubroName.toLowerCase();

                if (rubroKey) {
                    if (rubroNameToId[rubroKey]) {
                        rubroId = rubroNameToId[rubroKey];
                    } else {
                        // Create new rubro
                        const newId = await onAddRubro({
                            nombre: cleanRubroName,
                            obraId: obra?.id || '',
                            order: rubros.length + Object.keys(rubroNameToId).length,
                            duracionDias: 1,
                            fechaInicioEstimada: new Date().toISOString().split('T')[0]
                        });
                        rubroNameToId[rubroKey] = newId;
                        rubroId = newId;
                    }
                }

                await onAdd({
                    nombre: item.nombre,
                    unidadMedida: item.unidadMedida,
                    cantidad: item.cantidad,
                    cantidadReal: 0,
                    precioUnitario: item.precioUnitario,
                    costoManoObra: 0,
                    costoMateriales: 0,
                    obraId: obra?.id || '',
                    duracionDias: 1,
                    fechaInicioEstimada: new Date().toISOString().split('T')[0],
                    rubroId: rubroId,
                    completado: false
                });
            }
            setOpenPhotoAnalyzer(false);
            setPhotoPreview(null);
            setPhotoBase64(null);
            setAnalyzedItems([]);
        } catch (err: any) {
            setAnalyzeError(err.message || 'Error al agregar ítems.');
        } finally {
            setAddingItems(false);
        }
    };

    const handleClosePhotoAnalyzer = () => {
        setOpenPhotoAnalyzer(false);
        setPhotoPreview(null);
        setPhotoBase64(null);
        setAnalyzedItems([]);
        setAnalyzeError(null);
    };

    const handleCreateRubro = async () => {
        if (newRubroName && obra) {
            if (editingRubro) {
                await onUpdateRubro(editingRubro.id, { nombre: newRubroName });
            } else {
                await onAddRubro({
                    nombre: newRubroName,
                    obraId: obra.id,
                    order: rubros.length,
                    duracionDias: 10,
                    fechaInicioEstimada: new Date().toISOString().split('T')[0]
                });
            }
            setOpenAddRubro(false);
            setNewRubroName('');
            setEditingRubro(null);
        }
    };

    const groupedItems = useMemo(() => {
        const groups: { [key: string]: ItemPresupuesto[] } = {};
        rubros.forEach(r => groups[r.id] = []);
        groups['uncategorized'] = [];

        items.forEach(item => {
            if (item.rubroId && groups[item.rubroId]) {
                groups[item.rubroId].push(item);
            } else {
                groups['uncategorized'].push(item);
            }
        });
        return groups;
    }, [items, rubros]);

    const tableTotals = useMemo(() => {
        let venta = 0;
        let costoMO = 0;
        let costoMat = 0;
        items.forEach(item => {
            venta += item.cantidad * item.precioUnitario;
            costoMO += item.cantidad * (item.costoManoObra || 0);
            costoMat += item.cantidad * (item.costoMateriales || 0);
        });
        return { venta, costoMO, costoMat, ganancia: venta - (costoMO + costoMat) };
    }, [items]);

    const handleToggleCompletado = async (item: ItemPresupuesto) => {
        if (readOnly) return;
        await onUpdate(item.id, { completado: !item.completado });
    };

    const renderRow = (item: ItemPresupuesto, index: number) => {
        const totalVenta = item.cantidad * item.precioUnitario;
        const totalCostoMO = item.cantidad * (item.costoManoObra || 0);
        const totalCostoMat = item.cantidad * (item.costoMateriales || 0);
        const ganancia = totalVenta - (totalCostoMO + totalCostoMat);
        const isCompleted = item.completado;

        return (
            <Draggable key={item.id} draggableId={item.id} index={index} isDragDisabled={readOnly}>
                {(provided: DraggableProvided) => (
                    <TableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{ 
                            bgcolor: isCompleted ? 'rgba(63, 81, 181, 0.04)' : 'transparent',
                            transition: 'background-color 0.2s ease',
                            '&:hover': { 
                                bgcolor: isCompleted ? 'rgba(63, 81, 181, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                                '& .row-actions': { opacity: 1 }
                            },
                            '& td': {
                                borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                                py: 1.5
                            }
                        }}
                    >
                        {isBulkSelectionMode && (
                            <TableCell 
                                sx={{ 
                                    width: 40, 
                                    p: 0, 
                                    position: 'sticky', 
                                    left: 0, 
                                    bgcolor: isCompleted ? '#fbfcfe' : 'white', 
                                    zIndex: 11, 
                                    textAlign: 'center' 
                                }}
                            >
                                {!readOnly && (
                                    <Checkbox
                                        size="small"
                                        checked={selectedItemIds.has(item.id)}
                                        onChange={() => toggleSelectItem(item.id)}
                                    />
                                )}
                            </TableCell>
                        )}
                        <TableCell sx={{ 
                            minWidth: stickyColumnWidth, 
                            position: 'sticky', 
                            left: isBulkSelectionMode ? 40 : 0, 
                            bgcolor: isCompleted ? '#fbfcfe' : 'white', 
                            zIndex: 10,
                            boxShadow: '4px 0 8px rgba(0,0,0,0.02)'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {!readOnly && (
                                    <Box {...provided.dragHandleProps} sx={{ color: 'grey.400', cursor: 'grab', display: 'flex', alignItems: 'center', '&:hover': { color: 'primary.main' } }}>
                                        <DragIndicatorIcon fontSize="small" />
                                    </Box>
                                )}
                                <IconButton 
                                    size="small" 
                                    onClick={() => handleToggleCompletado(item)}
                                    disabled={readOnly}
                                    sx={{ 
                                        color: isCompleted ? 'success.main' : 'grey.300',
                                        transition: 'all 0.2s',
                                        '&:hover': { color: isCompleted ? 'success.dark' : 'success.light' }
                                    }}
                                >
                                    {isCompleted ? <CheckCircleOutlineIcon fontSize="small" /> : <RemoveCircleOutlineIcon fontSize="small" />}
                                </IconButton>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        textDecoration: isCompleted ? 'line-through' : 'none',
                                        color: isCompleted ? 'text.secondary' : 'text.primary',
                                        fontWeight: 600,
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    {item.nombre}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>{item.unidadMedida}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>{item.cantidad}</TableCell>
                        <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 600 }}>{item.cantidadReal || 0}</TableCell>
                        <TableCell align="right" sx={{ color: 'grey.600' }}>${item.precioUnitario.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                            ${totalVenta.toLocaleString()}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'grey.600', fontSize: '0.85rem' }}>${totalCostoMO.toLocaleString()}</TableCell>
                        <TableCell align="right" sx={{ color: 'grey.600', fontSize: '0.85rem' }}>${totalCostoMat.toLocaleString()}</TableCell>
                        <TableCell align="right">
                            <Typography variant="body2" sx={{ color: ganancia >= 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                                ${ganancia.toLocaleString()}
                            </Typography>
                        </TableCell>
                        <TableCell align="center">
                            {!readOnly && (
                                <Box className="row-actions" sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    gap: 0.5,
                                    opacity: 0.4,
                                    transition: 'opacity 0.2s'
                                }}>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => openEditDialog(item)}
                                        sx={{ color: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}
                                    >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => onDelete(item.id)} 
                                        sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.50' } }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>
                            )}
                        </TableCell>
                    </TableRow>
                )}
            </Draggable>
        );
    };



    const renderDesktopView = () => (
        <DragDropContext onDragEnd={onDragEnd}>
            <TableContainer 
                component={Paper} 
                elevation={0} 
                sx={{ 
                    borderRadius: 4, 
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    background: 'linear-gradient(to bottom, #ffffff, #fcfcfc)',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)'
                }}
            >
                <Droppable droppableId="all-rubros" type="RUBRO">
                    {(providedRubros: DroppableProvided) => (
                        <Table ref={providedRubros.innerRef} {...providedRubros.droppableProps} sx={{ minWidth: 900 }} size="small">
                            <TableHead>
                                <TableRow sx={{ 
                                    bgcolor: 'rgba(244, 246, 248, 0.8)',
                                    backdropFilter: 'blur(8px)',
                                    '& th': {
                                        py: 2,
                                        color: 'grey.700',
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        borderBottom: '2px solid rgba(0, 0, 0, 0.05)'
                                    }
                                }}>
                                    {isBulkSelectionMode && (
                                        <TableCell sx={{
                                            width: 40,
                                            p: 0,
                                            position: 'sticky',
                                            left: 0,
                                            bgcolor: '#f4f6f8',
                                            zIndex: 11,
                                            textAlign: 'center'
                                        }}>
                                            {!readOnly && (
                                                <Checkbox
                                                    size="small"
                                                    indeterminate={
                                                        (selectedItemIds.size > 0 || selectedRubroIds.size > 0) &&
                                                        (selectedItemIds.size < items.length || selectedRubroIds.size < rubros.length)
                                                    }
                                                    checked={(items.length > 0 || rubros.length > 0) && selectedItemIds.size === items.length && selectedRubroIds.size === rubros.length}
                                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                                />
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell sx={{
                                        minWidth: stickyColumnWidth,
                                        position: 'sticky',
                                        left: isBulkSelectionMode ? 40 : 0,
                                        bgcolor: '#f4f6f8',
                                        zIndex: 10,
                                        boxShadow: '2px 0 5px rgba(0,0,0,0.03)'
                                    }}>
                                        Descripción del Ítem
                                    </TableCell>
                                    <TableCell>Unidad</TableCell>
                                    <TableCell align="right">Cant. Presup.</TableCell>
                                    <TableCell align="right">Cant. Real</TableCell>
                                    <TableCell align="right">Precio Unit.</TableCell>
                                    <TableCell align="right" sx={{ color: 'primary.main' }}>Venta Total</TableCell>
                                    <TableCell align="right">Costo MO</TableCell>
                                    <TableCell align="right">Costo Mat.</TableCell>
                                    <TableCell align="right">Ganancia</TableCell>
                                    <TableCell align="center">Acciones</TableCell>
                                </TableRow>
                            </TableHead>

                            <Droppable droppableId="uncategorized" type="ITEM">
                                {(provided: DroppableProvided) => (
                                    <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                                        {groupedItems['uncategorized']?.length > 0 && (
                                            <TableRow>
                                                {isBulkSelectionMode && (
                                                    <TableCell
                                                        sx={{
                                                            width: 40,
                                                            p: 0,
                                                            position: 'sticky',
                                                            left: 0,
                                                            bgcolor: 'grey.50',
                                                            zIndex: 5,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {!readOnly && (
                                                            <Checkbox
                                                                size="small"
                                                                checked={groupedItems['uncategorized']?.length > 0 && groupedItems['uncategorized']?.every(i => selectedItemIds.has(i.id))}
                                                                indeterminate={
                                                                    groupedItems['uncategorized']?.some(i => selectedItemIds.has(i.id)) &&
                                                                    !groupedItems['uncategorized']?.every(i => selectedItemIds.has(i.id))
                                                                }
                                                                onChange={(e) => {
                                                                    const newSet = new Set(selectedItemIds);
                                                                    groupedItems['uncategorized'].forEach(i => {
                                                                        if (e.target.checked) newSet.add(i.id);
                                                                        else newSet.delete(i.id);
                                                                    });
                                                                    setSelectedItemIds(newSet);
                                                                }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                )}
                                                <TableCell colSpan={10} sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)', py: 1.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CategoryIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                                                                SIN RUBRO ESPECÍFICO
                                                            </Typography>
                                                        </Box>
                                                        {!readOnly && (
                                                            <Button 
                                                                size="small" 
                                                                startIcon={<AddIcon />} 
                                                                onClick={() => openAddItemWithRubro('')}
                                                                sx={{ borderRadius: 2, textTransform: 'none' }}
                                                            >
                                                                Agregar Ítem
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {groupedItems['uncategorized']?.map((item, index) => renderRow(item, index))}
                                        {provided.placeholder}
                                    </TableBody>
                                )}
                            </Droppable>

                            {rubros.map((rubro, index) => {
                                const rubroItems = groupedItems[rubro.id] || [];
                                const completedCount = rubroItems.filter(i => i.completado).length;
                                const progress = rubroItems.length > 0 ? (completedCount / rubroItems.length) * 100 : 0;

                                return (
                                    <Draggable key={rubro.id} draggableId={`rubro-${rubro.id}`} index={index} isDragDisabled={readOnly}>
                                        {(providedDraggable: DraggableProvided) => (
                                            <Droppable droppableId={rubro.id} type="ITEM">
                                                {(providedDroppable: DroppableProvided) => (
                                                    <TableBody 
                                                        ref={(el: HTMLTableSectionElement | null) => {
                                                            providedDraggable.innerRef(el);
                                                            providedDroppable.innerRef(el);
                                                        }} 
                                                        {...providedDraggable.draggableProps}
                                                        {...providedDroppable.droppableProps}
                                                    >
                                                        <TableRow sx={{ 
                                                            background: 'linear-gradient(90deg, rgba(63, 81, 181, 0.08) 0%, rgba(255, 255, 255, 0) 100%)',
                                                            position: 'relative',
                                                            '& td': { py: 1, borderBottom: 'none' }
                                                        }}>
                                                            {isBulkSelectionMode && (
                                                                <TableCell
                                                                    sx={{
                                                                        width: 40,
                                                                        p: 0,
                                                                        position: 'sticky',
                                                                        left: 0,
                                                                        bgcolor: 'rgba(240, 242, 249, 1)',
                                                                        zIndex: 5,
                                                                        textAlign: 'center'
                                                                    }}
                                                                >
                                                                    {!readOnly && (
                                                                        <Checkbox
                                                                            size="small"
                                                                            checked={selectedRubroIds.has(rubro.id)}
                                                                            onChange={() => toggleSelectRubro(rubro.id, rubroItems)}
                                                                        />
                                                                    )}
                                                                </TableCell>
                                                            )}
                                                            <TableCell colSpan={10} sx={{ 
                                                                position: 'sticky',
                                                                left: isBulkSelectionMode ? 40 : 0,
                                                                zIndex: 4,
                                                            }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
                                                                        {!readOnly && (
                                                                            <Box {...providedDraggable.dragHandleProps} sx={{ color: 'primary.light', cursor: 'grab', display: 'flex', alignItems: 'center', '&:hover': { color: 'primary.main' } }}>
                                                                                <DragIndicatorIcon fontSize="small" />
                                                                            </Box>
                                                                        )}
                                                                        <Box sx={{ 
                                                                            width: 36, 
                                                                            height: 36, 
                                                                            borderRadius: 1.5, 
                                                                            bgcolor: 'primary.main', 
                                                                            color: 'white',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            boxShadow: '0 4px 10px rgba(63, 81, 181, 0.3)'
                                                                        }}>
                                                                            <CategoryIcon fontSize="small" />
                                                                        </Box>
                                                                        <Box sx={{ flexGrow: 1 }}>
                                                                            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.dark', lineHeight: 1.2 }}>
                                                                                {rubro.nombre.toUpperCase()}
                                                                            </Typography>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                                                                <Box sx={{ width: 120 }}>
                                                                                    <LinearProgress 
                                                                                        variant="determinate" 
                                                                                        value={progress} 
                                                                                        sx={{ 
                                                                                            height: 6, 
                                                                                            borderRadius: 3,
                                                                                            bgcolor: 'rgba(0, 0, 0, 0.05)',
                                                                                            '& .MuiLinearProgress-bar': { borderRadius: 3 }
                                                                                        }} 
                                                                                    />
                                                                                </Box>
                                                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                                                    {completedCount}/{rubroItems.length} ítems
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    </Box>

                                                                    {!readOnly && (
                                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                                            <Button 
                                                                                variant="contained" 
                                                                                size="small" 
                                                                                startIcon={<AddIcon />} 
                                                                                onClick={() => openAddItemWithRubro(rubro.id)}
                                                                                sx={{ borderRadius: 2, textTransform: 'none', boxShadow: 'none' }}
                                                                            >
                                                                                Agregar Ítem
                                                                            </Button>
                                                                            <Box sx={{ display: 'flex' }}>
                                                                                <IconButton 
                                                                                    size="small" 
                                                                                    onClick={() => {
                                                                                        setEditingRubro(rubro);
                                                                                        setNewRubroName(rubro.nombre);
                                                                                        setOpenAddRubro(true);
                                                                                    }}
                                                                                    sx={{ color: 'grey.600', '&:hover': { color: 'primary.main', bgcolor: 'white' } }}
                                                                                >
                                                                                    <EditIcon fontSize="small" />
                                                                                </IconButton>
                                                                                <IconButton 
                                                                                    size="small" 
                                                                                    onClick={() => onDeleteRubro(rubro.id)} 
                                                                                    sx={{ color: 'grey.400', '&:hover': { color: 'error.main', bgcolor: 'white' } }}
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Box>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                        {rubroItems.map((item, idx) => renderRow(item, idx))}
                                                        {providedDroppable.placeholder}
                                                    </TableBody>
                                                )}
                                            </Droppable>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {providedRubros.placeholder}

                            <TableBody>
                                {items.length > 0 && (
                                    <TableRow sx={{ 
                                        bgcolor: 'primary.900', 
                                        '& td': { 
                                            py: 2.5, 
                                            color: 'white',
                                            border: 'none'
                                        } 
                                    }}>
                                        {isBulkSelectionMode && <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'primary.900', zIndex: 11 }}></TableCell>}
                                        <TableCell
                                            colSpan={1}
                                            sx={{
                                                fontWeight: 800,
                                                position: 'sticky',
                                                left: isBulkSelectionMode ? 40 : 0,
                                                bgcolor: 'primary.900',
                                                zIndex: 10,
                                                fontSize: '1rem',
                                                letterSpacing: 1
                                            }}
                                        >
                                            RESUMEN TOTAL
                                        </TableCell>
                                        <TableCell colSpan={3}></TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800, color: 'white', fontSize: '1.1rem' }}>
                                            ${tableTotals.venta.toLocaleString()}
                                        </TableCell>
                                        <TableCell colSpan={2}></TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                                            <Box sx={{ 
                                                display: 'inline-block', 
                                                px: 2, 
                                                py: 0.5, 
                                                borderRadius: 2, 
                                                bgcolor: tableTotals.ganancia >= 0 ? 'success.main' : 'error.main',
                                                color: 'white',
                                                fontSize: '1.1rem'
                                            }}>
                                                ${tableTotals.ganancia.toLocaleString()}
                                            </Box>
                                        </TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Droppable>
            </TableContainer>
        </DragDropContext>
    );

    const renderSummarySection = () => {
        const completedItems = items.filter(i => i.completado).length;
        const totalItems = items.length;
        const progress = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

        const cardStyle = {
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)',
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 15px 35px rgba(0, 0, 0, 0.08)',
            }
        };

        const iconBoxStyle = {
            position: 'absolute',
            top: -15,
            right: -15,
            opacity: 0.08,
            transform: 'rotate(-15deg)',
            transition: 'transform 0.3s ease',
            '& svg': { fontSize: 100 }
        };

        return (
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {/* Venta Total */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                        <Box sx={iconBoxStyle} className="icon-bg">
                            <ShoppingCartIcon />
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, display: 'block', mb: 1 }}>
                                Venta Proyectada
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: 'primary.900', letterSpacing: '-0.02em' }}>
                                ${tableTotals.venta.toLocaleString()}
                            </Typography>
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ px: 1, py: 0.25, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.100' }}>
                                    <Typography variant="caption" sx={{ color: 'primary.dark', fontWeight: 700 }}>
                                        Ingreso Bruto
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Costo Total */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                        <Box sx={iconBoxStyle}>
                            <PrecisionManufacturingIcon />
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="caption" sx={{ color: 'grey.600', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, display: 'block', mb: 1 }}>
                                Inversión Total
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: 'grey.900', letterSpacing: '-0.02em' }}>
                                ${(tableTotals.costoMO + tableTotals.costoMat).toLocaleString()}
                            </Typography>
                            <Box sx={{ mt: 2, display: 'flex', gap: 1.5 }}>
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600 }}>Mano Obra</Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>${tableTotals.costoMO.toLocaleString()}</Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem sx={{ opacity: 0.5 }} />
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600 }}>Materiales</Typography>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>${tableTotals.costoMat.toLocaleString()}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Ganancia */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ 
                        ...cardStyle,
                        background: tableTotals.ganancia >= 0 
                            ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(76, 175, 80, 0.03) 100%)' 
                            : 'linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(244, 67, 54, 0.03) 100%)',
                        borderColor: tableTotals.ganancia >= 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'
                    }}>
                        <Box sx={{ ...iconBoxStyle, opacity: 0.12 }}>
                            <SavingsIcon sx={{ color: tableTotals.ganancia >= 0 ? 'success.main' : 'error.main' }} />
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="caption" sx={{ color: tableTotals.ganancia >= 0 ? 'success.dark' : 'error.dark', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, display: 'block', mb: 1 }}>
                                Rentabilidad
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: tableTotals.ganancia >= 0 ? 'success.main' : 'error.main', letterSpacing: '-0.02em' }}>
                                ${tableTotals.ganancia.toLocaleString()}
                            </Typography>
                            <Box sx={{ mt: 1.5 }}>
                                <Chip 
                                    label={`${tableTotals.venta > 0 ? ((tableTotals.ganancia / tableTotals.venta) * 100).toFixed(1) : 0}% margen`}
                                    size="small"
                                    sx={{ 
                                        fontWeight: 800, 
                                        bgcolor: tableTotals.ganancia >= 0 ? 'success.main' : 'error.main',
                                        color: 'white',
                                        fontSize: '0.7rem'
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Avance */}
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={cardStyle}>
                        <Box sx={iconBoxStyle}>
                            <AssignmentTurnedInIcon />
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="caption" sx={{ color: 'grey.600', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1.5, display: 'block', mb: 1 }}>
                                Estado de Obra
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography variant="h4" sx={{ fontWeight: 900, color: 'primary.main', letterSpacing: '-0.02em' }}>
                                    {Math.round(progress)}%
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                    COMPLETADO
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={progress} 
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        bgcolor: 'rgba(0,0,0,0.05)',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            background: 'linear-gradient(90deg, #1a237e 0%, #2196f3 100%)',
                                            boxShadow: '0 2px 5px rgba(26, 35, 126, 0.3)'
                                        }
                                    }} 
                                />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mt: 1, display: 'block' }}>
                                    {completedItems} de {totalItems} ítems finalizados
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };


    const renderMobileItemCard = (item: ItemPresupuesto) => {
        const totalVenta = item.cantidad * item.precioUnitario;
        const totalCostoMO = item.cantidad * (item.costoManoObra || 0);
        const totalCostoMat = item.cantidad * (item.costoMateriales || 0);
        const ganancia = totalVenta - (totalCostoMO + totalCostoMat);
        const isCompleted = item.completado;

        return (
            <Card key={item.id} sx={{ 
                mb: 2, 
                borderRadius: 4, 
                background: isCompleted ? 'rgba(63, 81, 181, 0.05)' : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(12px)',
                border: '1px solid', 
                borderColor: isCompleted ? 'rgba(63, 81, 181, 0.2)' : 'rgba(255, 255, 255, 0.4)',
                boxShadow: isCompleted ? 'none' : '0 10px 25px rgba(0,0,0,0.04)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:active': { transform: isBulkSelectionMode ? 'none' : 'scale(0.98)' },
                position: 'relative',
                overflow: 'hidden'
            }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isBulkSelectionMode ? (
                                <Checkbox
                                    size="small"
                                    checked={selectedItemIds.has(item.id)}
                                    onChange={() => toggleSelectItem(item.id)}
                                    sx={{ p: 0 }}
                                />
                            ) : (
                                <Checkbox 
                                    checked={isCompleted || false} 
                                    onChange={() => handleToggleCompletado(item)}
                                    disabled={readOnly}
                                    size="small"
                                    sx={{ 
                                        p: 0,
                                        color: 'primary.light',
                                        '&.Mui-checked': { color: 'primary.main' }
                                     }}
                                />
                            )}
                        </Box>
                        
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                    lineHeight: 1.2, 
                                    fontWeight: 800,
                                    textDecoration: isCompleted ? 'line-through' : 'none',
                                    color: isCompleted ? 'text.secondary' : 'text.primary',
                                    fontSize: '1rem',
                                    letterSpacing: '-0.01em'
                                }}
                            >
                                {item.nombre}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip 
                                    label={`Pres: ${item.cantidad} ${item.unidadMedida}`} 
                                    size="small" 
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.6rem', 
                                        fontWeight: 700,
                                        bgcolor: isCompleted ? 'rgba(0,0,0,0.05)' : 'rgba(0, 0, 0, 0.04)',
                                        color: 'text.secondary',
                                        borderRadius: 1,
                                        '& .MuiChip-label': { px: 1 }
                                    }} 
                                />
                                <Chip 
                                    label={`Real: ${item.cantidadReal || 0} ${item.unidadMedida}`} 
                                    size="small" 
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.6rem', 
                                        fontWeight: 800,
                                        bgcolor: isCompleted ? 'rgba(63, 81, 181, 0.05)' : 'rgba(63, 81, 181, 0.1)',
                                        color: 'primary.main',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'rgba(63, 81, 181, 0.2)',
                                        '& .MuiChip-label': { px: 1 }
                                    }} 
                                />
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, ml: 'auto' }}>
                                    ${item.precioUnitario.toLocaleString()} / {item.unidadMedida}
                                </Typography>
                            </Box>
                        </Box>
                        
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isCompleted ? 'text.secondary' : 'primary.main' }}>
                                ${totalVenta.toLocaleString()}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <Typography variant="caption" sx={{ 
                                    color: ganancia >= 0 ? 'success.main' : 'error.main', 
                                    fontWeight: 700,
                                    fontSize: '0.65rem'
                                }}>
                                    {ganancia >= 0 ? '+' : ''}${ganancia.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5 }}>
                        <Box sx={{ borderTop: '1px dashed rgba(0,0,0,0.05)', pt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                <EngineeringIcon sx={{ fontSize: 10, color: 'text.disabled' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase' }}>Mano de Obra</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>${totalCostoMO.toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ borderTop: '1px dashed rgba(0,0,0,0.05)', pt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                <ConstructionIcon sx={{ fontSize: 10, color: 'text.disabled' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase' }}>Materiales</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>${totalCostoMat.toLocaleString()}</Typography>
                        </Box>
                    </Box>

                    {!readOnly && !isBulkSelectionMode && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 1 }}>
                            <Button 
                                size="small" 
                                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                                onClick={() => openEditDialog(item)}
                                sx={{ 
                                    fontSize: '0.7rem', 
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    color: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.50' }
                                }}
                            >
                                Editar
                            </Button>
                            <Button 
                                size="small" 
                                color="error" 
                                startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                onClick={() => onDelete(item.id)}
                                sx={{ 
                                    fontSize: '0.7rem', 
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    color: 'error.light',
                                    '&:hover': { color: 'error.main', bgcolor: 'error.50' }
                                }}
                            >
                                Eliminar
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    const renderMobileView = () => {
        return (
            <Box>
                {/* Uncategorized section */}
            {groupedItems['uncategorized']?.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                        px: 1.5, 
                        py: 1,
                        mb: 1.5, 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.03)',
                        borderRadius: 2
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InventoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Sin Rubro Específico
                            </Typography>
                        </Box>
                        {!readOnly && (
                            <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={() => openAddItemWithRubro('')}
                                sx={{ bgcolor: 'white', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}
                            >
                                <AddIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        )}
                    </Box>
                    {groupedItems['uncategorized'].map(item => renderMobileItemCard(item))}
                </Box>
            )}

            {/* Empty State */}
            {items.length === 0 && rubros.length === 0 && (
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    py: 10, 
                    px: 4,
                    textAlign: 'center',
                    background: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 6,
                    border: '2px dashed rgba(0, 0, 0, 0.08)',
                    mt: 2
                }}>
                    <Box sx={{ 
                        width: 80, 
                        height: 80, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.50', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mb: 3,
                        boxShadow: '0 10px 20px rgba(0,0,0,0.03)'
                    }}>
                        <InventoryIcon sx={{ fontSize: 40, color: 'primary.light', opacity: 0.8 }} />
                    </Box>
                    <Typography variant="h6" color="primary.900" gutterBottom sx={{ fontWeight: 900, letterSpacing: -0.5 }}>
                        Comienza tu presupuesto
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280, mb: 4, lineHeight: 1.6, fontWeight: 500 }}>
                        Agrega rubros e ítems para organizar los costos y materiales de tu obra de manera profesional.
                    </Typography>
                    {!readOnly && (
                        <Box sx={{ display: 'flex', gap: 2, width: '100%', justifyContent: 'center' }}>
                            <Button 
                                variant="outlined" 
                                startIcon={<AddIcon />} 
                                onClick={() => { setEditingRubro(null); setNewRubroName(''); setOpenAddRubro(true); }}
                                sx={{ borderRadius: 3, px: 3, py: 1, textTransform: 'none', fontWeight: 700 }}
                            >
                                Rubro
                            </Button>
                            <Button 
                                variant="contained" 
                                startIcon={<AddIcon />} 
                                onClick={() => { setEditingItem(null); setNewItem({ nombre: '', unidadMedida: '', cantidad: 0, cantidadReal: 0, precioUnitario: 0, costoManoObra: 0, costoMateriales: 0, duracionDias: 1, fechaInicioEstimada: new Date().toISOString().split('T')[0], rubroId: '' }); setOpenAdd(true); }}
                                sx={{ borderRadius: 3, px: 3, py: 1, textTransform: 'none', fontWeight: 700, boxShadow: '0 4px 12px rgba(63, 81, 181, 0.2)' }}
                            >
                                Ítem
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {/* Rubros as Accordions */}
            {rubros.map(rubro => {
                const rubroItems = groupedItems[rubro.id] || [];
                const completedCount = rubroItems.filter(i => i.completado).length;
                const progress = rubroItems.length > 0 ? (completedCount / rubroItems.length) * 100 : 0;
                
                return (
                    <Accordion 
                        key={rubro.id} 
                        defaultExpanded 
                        sx={{ 
                            border: 'none', 
                            boxShadow: 'none', 
                            bgcolor: 'transparent', 
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': { m: 0 }
                        }}
                    >
                        <AccordionSummary 
                            expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                            sx={{ 
                                px: 2, 
                                minHeight: 64, 
                                '&.Mui-expanded': { minHeight: 64 },
                                background: 'rgba(255, 255, 255, 0.7)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: 3,
                                mb: 1.5,
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                }
                            }}
                        >
                            <Box sx={{ 
                                position: 'absolute', 
                                bottom: 0, 
                                left: 0, 
                                height: 4, 
                                background: 'linear-gradient(90deg, #3f51b5, #2196f3)', 
                                width: `${progress}%`,
                                transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                                opacity: 0.8
                            }} />
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                {isBulkSelectionMode && (
                                    <Checkbox
                                        size="small"
                                        checked={selectedRubroIds.has(rubro.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => toggleSelectRubro(rubro.id, rubroItems)}
                                        sx={{ p: 0 }}
                                    />
                                )}
                                <Box sx={{ 
                                    width: 38, 
                                    height: 38, 
                                    borderRadius: 1.5, 
                                    bgcolor: 'primary.main', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    color: 'white',
                                    boxShadow: '0 4px 10px rgba(63, 81, 181, 0.2)'
                                }}>
                                    <CategoryIcon fontSize="small" />
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
                                        {rubro.nombre}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                        {completedCount}/{rubroItems.length} ítems • {Math.round(progress)}%
                                    </Typography>
                                </Box>
                                {!isBulkSelectionMode && !readOnly && (
                                    <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                        <IconButton size="small" color="primary" onClick={() => openAddItemWithRubro(rubro.id)} sx={{ bgcolor: 'white', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                                            <AddIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => {
                                            setEditingRubro(rubro);
                                            setNewRubroName(rubro.nombre);
                                            setOpenAddRubro(true);
                                        }} sx={{ bgcolor: 'white', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                                            <EditIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 0.5, py: 0 }}>
                            {rubroItems.map(item => renderMobileItemCard(item))}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

if (!obra) return null;

    return (
        <Box>
            {!readOnly && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {isBulkSelectionMode ? (
                            <>
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        setSelectedItemIds(new Set());
                                        setSelectedRubroIds(new Set());
                                        setIsBulkSelectionMode(false);
                                    }}
                                    size="small"
                                    color="inherit"
                                    sx={{ borderRadius: 2 }}
                                >
                                    Cancelar
                                </Button>
                                {(selectedItemIds.size > 0 || selectedRubroIds.size > 0) && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => setOpenConfirmBulkDelete(true)}
                                        size="small"
                                        sx={{ borderRadius: 2 }}
                                    >
                                        Borrar ({selectedItemIds.size + selectedRubroIds.size})
                                    </Button>
                                )}
                            </>
                        ) : (
                            <Button
                                variant="outlined"
                                onClick={() => setIsBulkSelectionMode(true)}
                                startIcon={<CheckCircleOutlineIcon />}
                                color="inherit"
                                size="small"
                                sx={{ borderRadius: 2, borderColor: 'grey.300' }}
                            >
                                {isMobile ? "Seleccionar" : "Selección Masiva"}
                            </Button>
                        )}
                    </Box>

                    {!isMobile && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="outlined"
                                startIcon={<AutoAwesomeIcon />}
                                onClick={() => { setAnalyzedItems([]); setPhotoPreview(null); setPhotoBase64(null); setAnalyzeError(null); setOpenPhotoAnalyzer(true); }}
                                sx={{
                                    borderColor: 'secondary.main',
                                    color: 'secondary.main',
                                    borderRadius: 2,
                                    '&:hover': { bgcolor: 'secondary.50', borderColor: 'secondary.dark' }
                                }}
                                size="small"
                            >
                                Analizar con IA
                            </Button>
                            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => {
                                setEditingRubro(null);
                                setNewRubroName('');
                                setOpenAddRubro(true);
                            }} size="small" sx={{ borderRadius: 2 }}>
                                Agregar Rubro
                            </Button>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
                                setEditingItem(null);
                                setNewItem({ nombre: '', unidadMedida: '', cantidad: 0, cantidadReal: 0, precioUnitario: 0, costoManoObra: 0, costoMateriales: 0, duracionDias: 1, fechaInicioEstimada: new Date().toISOString().split('T')[0], rubroId: '' });
                                setOpenAdd(true);
                            }} size="small" sx={{ borderRadius: 2 }}>
                                Agregar Item
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {isMobile ? renderMobileView() : renderDesktopView()}

            {/* Floating Action Button (SpeedDial) for Mobile */}
            {isMobile && !readOnly && !isBulkSelectionMode && (
                <SpeedDial
                    ariaLabel="Acciones de presupuesto"
                    sx={{ position: 'fixed', bottom: 16, right: 16 }}
                    icon={<SpeedDialIcon />}
                    FabProps={{
                        sx: {
                            bgcolor: 'secondary.main',
                            '&:hover': { bgcolor: 'secondary.dark' },
                            boxShadow: '0 4px 20px rgba(0,0,0,0.25)'
                        }
                    }}
                >
                    <SpeedDialAction
                        icon={<AutoAwesomeIcon />}
                        tooltipTitle="Analizar con IA"
                        onClick={() => { setAnalyzedItems([]); setPhotoPreview(null); setPhotoBase64(null); setAnalyzeError(null); setOpenPhotoAnalyzer(true); }}
                        FabProps={{ sx: { bgcolor: 'secondary.50', color: 'secondary.main' } }}
                    />
                    <SpeedDialAction
                        icon={<AddIcon />}
                        tooltipTitle="Agregar Rubro"
                        onClick={() => { setEditingRubro(null); setNewRubroName(''); setOpenAddRubro(true); }}
                    />
                    <SpeedDialAction
                        icon={<AddIcon />}
                        tooltipTitle="Agregar Item"
                        onClick={() => { setEditingItem(null); setNewItem({ nombre: '', unidadMedida: '', cantidad: 0, cantidadReal: 0, precioUnitario: 0, costoManoObra: 0, costoMateriales: 0, duracionDias: 1, fechaInicioEstimada: new Date().toISOString().split('T')[0], rubroId: '' }); setOpenAdd(true); }}
                    />
                </SpeedDial>
            )}


            <Dialog 
                open={openAddRubro} 
                onClose={() => setOpenAddRubro(false)} 
                fullScreen={isSmallMobile}
                PaperProps={{
                    sx: { borderRadius: isSmallMobile ? 0 : 3 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CategoryIcon color="primary" />
                    {editingRubro ? 'Editar Rubro' : 'Nuevo Rubro'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Nombre del Rubro"
                            fullWidth
                            variant="outlined"
                            value={newRubroName}
                            onChange={(e) => setNewRubroName(e.target.value)}
                            InputProps={{
                                sx: { borderRadius: 2 }
                            }}
                            placeholder="Ej: Albañilería, Pintura, etc."
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 0 }}>
                    <Button onClick={() => setOpenAddRubro(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleCreateRubro} variant="contained" sx={{ borderRadius: 2, px: 3 }}>
                        {editingRubro ? 'Guardar' : 'Crear Rubro'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={openAdd} 
                onClose={() => setOpenAdd(false)} 
                fullScreen={isSmallMobile}
                PaperProps={{
                    sx: { borderRadius: isSmallMobile ? 0 : 3 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ConstructionIcon color="primary" />
                    {editingItem ? 'Editar Ítem' : 'Nuevo Ítem'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Nombre del Ítem" 
                                value={newItem.nombre} 
                                onChange={e => setNewItem({ ...newItem, nombre: e.target.value })}
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField 
                                fullWidth 
                                label="Presup." 
                                type="number" 
                                value={newItem.cantidad} 
                                onChange={e => setNewItem({ ...newItem, cantidad: Number(e.target.value) })}
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField 
                                fullWidth 
                                label="Real" 
                                type="number" 
                                value={newItem.cantidadReal} 
                                onChange={e => setNewItem({ ...newItem, cantidadReal: Number(e.target.value) })}
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField 
                                fullWidth 
                                label="Unidad" 
                                value={newItem.unidadMedida} 
                                onChange={e => setNewItem({ ...newItem, unidadMedida: e.target.value })}
                                placeholder="m2, un, etc."
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                label="Precio de Venta Unitario" 
                                type="number" 
                                value={newItem.precioUnitario} 
                                onChange={e => setNewItem({ ...newItem, precioUnitario: Number(e.target.value) })}
                                InputProps={{ 
                                    sx: { borderRadius: 2 },
                                    startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }}>
                                <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 700 }}>
                                    COSTOS (OPCIONAL)
                                </Typography>
                            </Divider>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField 
                                fullWidth 
                                label="Costo M.O. Unit." 
                                type="number" 
                                value={newItem.costoManoObra} 
                                onChange={e => setNewItem({ ...newItem, costoManoObra: Number(e.target.value) })}
                                InputProps={{ 
                                    sx: { borderRadius: 2 },
                                    startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField 
                                fullWidth 
                                label="Costo Mat. Unit." 
                                type="number" 
                                value={newItem.costoMateriales} 
                                onChange={e => setNewItem({ ...newItem, costoMateriales: Number(e.target.value) })}
                                InputProps={{ 
                                    sx: { borderRadius: 2 },
                                    startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography>
                                }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1 }}>
                    <Button onClick={() => setOpenAdd(false)} color="inherit">Cancelar</Button>
                    <Button onClick={handleCreateOrUpdate} variant="contained" sx={{ borderRadius: 2, px: 3 }}>
                        {editingItem ? 'Guardar Cambios' : 'Agregar Ítem'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Photo Analyzer Dialog */}
            <Dialog open={openPhotoAnalyzer} onClose={handleClosePhotoAnalyzer} maxWidth="md" fullWidth fullScreen={isSmallMobile}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon color="secondary" />
                    Analizar Presupuesto con IA
                </DialogTitle>
                <DialogContent dividers>
                    {/* Upload Zone */}
                    <Box
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                            border: '2px dashed',
                            borderColor: photoPreview ? 'secondary.main' : 'divider',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            bgcolor: photoPreview ? 'transparent' : 'grey.50',
                            mb: 2,
                            transition: 'all 0.2s',
                            '&:hover': { borderColor: 'secondary.main', bgcolor: 'secondary.50' }
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handlePhotoUpload}
                        />
                        {photoPreview ? (
                            <Box>
                                <img
                                    src={photoPreview}
                                    alt="Vista previa"
                                    style={{ maxHeight: 300, maxWidth: '100%', borderRadius: 8, objectFit: 'contain' }}
                                />
                                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Clic para cambiar la imagen
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <PhotoCameraIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                <Typography variant="body1" color="text.secondary">
                                    Subí una foto del presupuesto
                                </Typography>
                                <Typography variant="caption" color="text.disabled">
                                    JPG, PNG, WEBP — Clic para seleccionar
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Analyze Button */}
                    {photoPreview && analyzedItems.length === 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={analyzing ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                                onClick={handleAnalyzePhoto}
                                disabled={analyzing}
                                size="large"
                            >
                                {analyzing ? 'Analizando imagen…' : 'Analizar con Gemini'}
                            </Button>
                        </Box>
                    )}

                    {analyzing && <LinearProgress color="secondary" sx={{ mb: 2, borderRadius: 1 }} />}

                    {analyzeError && (
                        <Alert severity="error" sx={{ mb: 2 }}>{analyzeError}</Alert>
                    )}

                    {/* Results Table */}
                    {analyzedItems.length > 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    Ítems detectados
                                    <Chip label={analyzedItems.filter(i => i.selected).length} size="small" color="secondary" sx={{ ml: 1 }} />
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" startIcon={<CheckCircleOutlineIcon />} onClick={() => setAnalyzedItems(prev => prev.map(i => ({ ...i, selected: true })))}>
                                        Todos
                                    </Button>
                                    <Button size="small" startIcon={<RemoveCircleOutlineIcon />} onClick={() => setAnalyzedItems(prev => prev.map(i => ({ ...i, selected: false })))}>
                                        Ninguno
                                    </Button>
                                </Box>
                            </Box>
                            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                                            <TableCell padding="checkbox" />
                                            <TableCell sx={{ fontWeight: 600 }}>Rubro</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Unidad</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Cant.</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Precio U.</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analyzedItems.map((item, idx) => (
                                            <TableRow
                                                key={idx}
                                                hover
                                                sx={{ opacity: item.selected ? 1 : 0.4, transition: 'opacity 0.2s' }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={item.selected}
                                                        onChange={(e) => setAnalyzedItems(prev => prev.map((it, i) => i === idx ? { ...it, selected: e.target.checked } : it))}
                                                        size="small"
                                                        color="secondary"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        variant="standard"
                                                        value={item.rubro}
                                                        onChange={(e) => setAnalyzedItems(prev => prev.map((it, i) => i === idx ? { ...it, rubro: e.target.value } : it))}
                                                        InputProps={{ disableUnderline: true }}
                                                        fullWidth
                                                        sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        variant="standard"
                                                        value={item.nombre}
                                                        onChange={(e) => setAnalyzedItems(prev => prev.map((it, i) => i === idx ? { ...it, nombre: e.target.value } : it))}
                                                        InputProps={{ disableUnderline: true }}
                                                        fullWidth
                                                        sx={{ fontSize: '0.875rem' }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        variant="standard"
                                                        value={item.unidadMedida}
                                                        onChange={(e) => setAnalyzedItems(prev => prev.map((it, i) => i === idx ? { ...it, unidadMedida: e.target.value } : it))}
                                                        InputProps={{ disableUnderline: true }}
                                                        sx={{ width: 60 }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        variant="standard"
                                                        type="number"
                                                        value={item.cantidad}
                                                        onChange={(e) => setAnalyzedItems(prev => prev.map((it, i) => i === idx ? { ...it, cantidad: Number(e.target.value) } : it))}
                                                        InputProps={{ disableUnderline: true }}
                                                        inputProps={{ style: { textAlign: 'right' } }}
                                                        sx={{ width: 70 }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <TextField
                                                        variant="standard"
                                                        type="number"
                                                        value={item.precioUnitario}
                                                        onChange={(e) => setAnalyzedItems(prev => prev.map((it, i) => i === idx ? { ...it, precioUnitario: Number(e.target.value) } : it))}
                                                        InputProps={{ disableUnderline: true }}
                                                        inputProps={{ style: { textAlign: 'right' } }}
                                                        sx={{ width: 90 }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                                    ${(item.cantidad * item.precioUnitario).toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Podés editar los valores antes de agregar. Los campos en 0 no se encontraron en la imagen.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePhotoAnalyzer}>Cancelar</Button>
                    {analyzedItems.length > 0 && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleAddAnalyzedItems}
                            disabled={addingItems || analyzedItems.filter(i => i.selected).length === 0}
                            startIcon={addingItems ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                        >
                            {addingItems ? 'Agregando…' : `Agregar ${analyzedItems.filter(i => i.selected).length} ítem${analyzedItems.filter(i => i.selected).length !== 1 ? 's' : ''}`}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Bulk Delete Confirmation Dialog */}
            <Dialog open={openConfirmBulkDelete} onClose={() => setOpenConfirmBulkDelete(false)}>
                <DialogTitle>Confirmar eliminación masiva</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar los {selectedItemIds.size} ítems y {selectedRubroIds.size} rubros seleccionados?
                    </Typography>
                    {selectedRubroIds.size > 0 && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                            Nota: Los rubros se eliminarán y sus ítems (si no están seleccionados para borrar) quedarán sin categoría.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmBulkDelete(false)}>Cancelar</Button>
                    <Button onClick={handleBulkDelete} color="error" variant="contained">Eliminar todo</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
